import { supabase } from '../lib/supabase';

export interface NotificationCounts {
  total: number;
  messages: number;
  quotes: number;
  appointments: number;
  jobs: number;
  contracts: number;
  wheelEntries: number;
  wheelWins: number;
}

/**
 * Obtiene el conteo de notificaciones pendientes para el admin
 */
/**
 * Marca todas las notificaciones pendientes como revisadas/atendidas
 */
export async function dismissAllNotifications(): Promise<void> {
  try {
    await Promise.all([
      // Leads nuevos → en revisión
      supabase
        .from('leads')
        .update({ status: 'in_review' })
        .eq('status', 'new'),

      // Leads leídos → en revisión
      supabase
        .from('leads')
        .update({ status: 'in_review' })
        .eq('status', 'read'),

      // Citas solicitadas → confirmadas
      supabase
        .from('appointments')
        .update({ status: 'confirmed' })
        .eq('status', 'requested'),

      // Trabajos completados → marcar como revisados por admin
      supabase
        .from('jobs')
        .update({ admin_reviewed_at: new Date().toISOString() })
        .eq('status', 'completed')
        .not('client_completed_at', 'is', null)
        .is('admin_reviewed_at', null),

      // Contratos firmados → marcar como revisados por admin
      supabase
        .from('contracts')
        .update({ admin_reviewed_at: new Date().toISOString() })
        .eq('status', 'signed')
        .is('admin_reviewed_at', null),

      // Cotizaciones nuevas → Contactada
      supabase
        .from('cotizaciones')
        .update({ estado: 'Contactada', updated_at: new Date().toISOString() })
        .eq('estado', 'Nueva'),

      // Ruleta entradas → marcar como revisadas
      supabase
        .from('ruleta_giros')
        .update({ is_reviewed: true })
        .eq('is_reviewed', false),

      // Ruleta premios → marcar como reclamados
      supabase
        .from('ruleta_giros')
        .update({ reclamado: true })
        .eq('reclamado', false)
        .not('premio_nombre', 'is', null),
    ]);
  } catch (error) {
    console.error('Error al descartar notificaciones:', error);
    throw error;
  }
}

export async function getNotificationCounts(): Promise<NotificationCounts> {
  try {
    const [
      messagesResult,
      quotesResult,
      appointmentsResult,
      jobsResult,
      wheelEntriesResult,
      wheelWinsResult,
    ] = await Promise.all([
      // MENSAJES/LEADS - status = 'new'
      supabase
        .from('leads')
        .select('id', { count: 'exact' })
        .eq('status', 'new'),

      // COTIZACIONES - estado = 'Nueva' (recién llegadas del funnel)
      supabase
        .from('cotizaciones')
        .select('id', { count: 'exact' })
        .eq('estado', 'Nueva'),

      // CITAS - solo status = 'requested' (pendientes de confirmar)
      supabase
        .from('appointments')
        .select('id', { count: 'exact' })
        .eq('status', 'requested'),

      // TRABAJOS - completados por cliente, no revisados por admin
      supabase
        .from('jobs')
        .select('id', { count: 'exact' })
        .eq('status', 'completed')
        .not('client_completed_at', 'is', null)
        .is('admin_reviewed_at', null),

      // RULETA - entradas no revisadas
      supabase
        .from('ruleta_giros')
        .select('id', { count: 'exact' })
        .eq('is_reviewed', false),

      // RULETA - premios ganados no reclamados
      supabase
        .from('ruleta_giros')
        .select('id', { count: 'exact' })
        .eq('reclamado', false)
        .not('premio_nombre', 'is', null),
    ]);

    const messages = messagesResult.count ?? 0;
    const quotes = quotesResult.count ?? 0;
    const appointments = appointmentsResult.count ?? 0;
    const jobs = jobsResult.count ?? 0;
    const wheelEntries = wheelEntriesResult.count ?? 0;
    const wheelWins = wheelWinsResult.count ?? 0;

    // CONTRATOS - firmados y no revisados por admin
    const { count: contractsCount, error: contractsError } = await supabase
      .from('contracts')
      .select('id', { count: 'exact' })
      .eq('status', 'signed')
      .is('admin_reviewed_at', null);

    if (contractsError) {
      console.error('Error fetching contracts for notifications:', contractsError);
    }

    const contracts = contractsCount ?? 0;
    const total = messages + quotes + appointments + jobs + contracts + wheelEntries + wheelWins;

    return {
      total,
      messages,
      quotes,
      appointments,
      jobs,
      contracts,
      wheelEntries,
      wheelWins,
    };
  } catch (error) {
    console.error('Error obteniendo conteo de notificaciones:', error);
    return {
      total: 0,
      messages: 0,
      quotes: 0,
      appointments: 0,
      jobs: 0,
      contracts: 0,
      wheelEntries: 0,
      wheelWins: 0,
    };
  }
}
